import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { configureFormatting, formattingDefaults } from '@/lib/format';

export const DEFAULT_APP_NAME = 'Sara Eng';
export const DEFAULT_THEME = 'industrial';
export const DEFAULT_SHOP_DRAWING_DISCIPLINES = ['BMS', 'LCS', 'GRMS', 'HA', 'RPC_CONNECTIONS', 'ELV', 'OTHER'];
export const THEME_OPTIONS = [
  { value: DEFAULT_THEME, label: 'Sara Light' },
  { value: 'light_brand', label: 'White / Navy / Green' },
  { value: 'dark_brand', label: 'Navy Dark / Green' },
];

const VALID_THEMES = new Set(THEME_OPTIONS.map((option) => option.value));

/*
  Workspace defaults (Admin › Settings, wave 3 · 26 #8): the company's time zone, currency, work week and
  date format, from GET /api/settings `workspace`. The provider hands the time zone and the date format to
  lib/format.js (configureFormatting), so formatDate / formatDateTime / ages everywhere follow them (the currency is
  handed over too, but money does not follow it yet — workspaceCurrencyApplied() says when it does); pages read
  the rest with useWorkspace() — e.g. a Timeline weekend of workspace.weekend_days. Until the server answers (or for
  an older server) these are the company's defaults.
*/
export const DEFAULT_WORKSPACE = {
  time_zone: 'Asia/Dubai',
  currency: 'AED',
  work_week: 'mon_sat',
  date_format: 'd_mmm_yyyy',
  time_zone_label: 'Asia/Dubai · GST (UTC+4)',
  time_zone_short: 'GST',
  utc_offset_minutes: 240,
  currency_label: 'AED — UAE Dirham',
  working_days: [0, 1, 2, 3, 4, 5],
  work_week_label: 'Mon–Sat',
  date_format_example: '30 Sep 2026',
};

/** Server weekdays are Python's (0 = Monday … 6 = Sunday); JS Date.getDay() is 0 = Sunday … 6 = Saturday. */
const toJsWeekday = (day) => (Number(day) + 1) % 7;

export const normalizeWorkspace = (value = {}) => {
  const workspace = { ...DEFAULT_WORKSPACE, ...(value && typeof value === 'object' ? value : {}) };
  const working = Array.isArray(workspace.working_days) && workspace.working_days.length
    ? workspace.working_days.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)
    : DEFAULT_WORKSPACE.working_days;
  return {
    ...workspace,
    working_days: working,
    // the days off in JS getDay() numbers (Mon–Sat → [0] Sunday) — what lib/gantt's `weekend` option takes
    weekend_days: [0, 1, 2, 3, 4, 5, 6].filter((jsDay) => !working.map(toJsWeekday).includes(jsDay)),
  };
};

/** One discipline code as it will be saved: 'acs panel' → 'ACS_PANEL' (letters, digits and _ only). */
export const normalizeDisciplineToken = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

export const normalizeShopDrawingDisciplines = (values = []) => {
  const normalized = [];
  values.forEach((value) => {
    const token = normalizeDisciplineToken(value);
    if (token && !normalized.includes(token)) normalized.push(token);
  });
  if (!normalized.length) normalized.push(...DEFAULT_SHOP_DRAWING_DISCIPLINES);
  // OTHER is the fallback: always present and always the last option.
  return [...normalized.filter((token) => token !== 'OTHER'), 'OTHER'];
};

const AppSettingsContext = createContext(null);

const normalizeSettings = (settings = {}) => ({
  app_name: String(settings.app_name || DEFAULT_APP_NAME).trim() || DEFAULT_APP_NAME,
  theme: VALID_THEMES.has(settings.theme) ? settings.theme : DEFAULT_THEME,
  shop_drawing_disciplines: normalizeShopDrawingDisciplines(settings.shop_drawing_disciplines || DEFAULT_SHOP_DRAWING_DISCIPLINES),
  updated_at: settings.updated_at || '',
  updated_by: settings.updated_by || '',
  workspace: normalizeWorkspace(settings.workspace),
});

export function AppSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => normalizeSettings());
  const [loading, setLoading] = useState(true);
  // Admin › Settings theme preview (26 #2): only this browser shows it, until the admin saves or leaves.
  const [previewTheme, setPreviewThemeState] = useState(null);
  const setPreviewTheme = useCallback((value) => {
    setPreviewThemeState(VALID_THEMES.has(value) ? value : null);
  }, []);
  const appliedTheme = previewTheme && previewTheme !== settings.theme ? previewTheme : settings.theme;
  // "Exit preview" (the ribbon under the top bar) must also put the Settings draft back, not only the colours:
  // the Settings tab registers how (its draft theme → the saved one); without it the preview just ends.
  const stopPreviewRef = useRef(null);
  const registerStopPreview = useCallback((handler) => {
    stopPreviewRef.current = handler;
    return () => { if (stopPreviewRef.current === handler) stopPreviewRef.current = null; };
  }, []);
  const stopPreview = useCallback(() => {
    if (stopPreviewRef.current) stopPreviewRef.current();
    else setPreviewThemeState(null);
  }, []);

  const refreshSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings(normalizeSettings(res.data));
      return normalizeSettings(res.data);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (payload) => {
    const res = await api.put('/admin/settings', payload);
    const next = normalizeSettings(res.data);
    setSettings(next);
    // undo_id: the history entry of this save, for the "Settings saved · Undo" toast
    return { ...next, undo_id: res.data?.undo_id || '' };
  };

  useEffect(() => {
    refreshSettings().catch(() => setLoading(false));
  }, []);

  // 26 #8: every date and time in the app follows the company's time zone and date format (lib/format.js).
  // Applied while rendering (not in an effect), so the pages rendered with these settings already use them.
  // The currency goes along: lib/format.js ignores it until money formatting reads the workspace currency
  // (see workspaceCurrencyApplied below).
  const zone = settings.workspace.time_zone;
  const dateFormat = settings.workspace.date_format;
  const currency = settings.workspace.currency;
  useMemo(() => configureFormatting({ timeZone: zone, dateFormat, currency }), [zone, dateFormat, currency]);

  useEffect(() => {
    document.title = settings.app_name;
  }, [settings.app_name]);

  useEffect(() => {
    document.documentElement.dataset.theme = appliedTheme;
    document.documentElement.style.colorScheme = appliedTheme === 'dark_brand' ? 'dark' : 'light';
  }, [appliedTheme]);

  const value = useMemo(() => ({
    appName: settings.app_name,
    theme: settings.theme,
    workspace: settings.workspace,
    settings,
    settingsLoading: loading,
    refreshSettings,
    updateSettings,
    previewTheme: appliedTheme !== settings.theme ? appliedTheme : null,
    setPreviewTheme,
    stopPreview,
    registerStopPreview,
  }), [settings, loading, appliedTheme, setPreviewTheme, stopPreview, registerStopPreview]);

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export const useAppSettings = () => useContext(AppSettingsContext);

/** The company's workspace defaults (26 #8); the built-in defaults outside the provider (isolated tests). */
export const useWorkspace = () => useContext(AppSettingsContext)?.workspace || normalizeWorkspace();

/**
 * Whether money in the app follows the workspace currency (26 #8). True once lib/format.js applies the `currency`
 * this provider hands to configureFormatting — its defaults then carry `currency` — and the AED column headers read
 * useWorkspace().currency. Until then Admin › Settings shows the Currency select read-only with "Soon".
 */
export const workspaceCurrencyApplied = () => Object.prototype.hasOwnProperty.call(formattingDefaults(), 'currency');
